import React, { useState, useEffect } from 'react';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import StatCard from '../charts/StatCard';
import GaugeChart from '../charts/GaugeChart';
import { ShieldCheckIcon, CurrencyDollarIcon, ClockIcon } from '@heroicons/react/24/outline';
import api from '../../../services/api';

interface InsuranceIndicatorsProps {
  dateRange: { start_date?: string; end_date?: string };
}

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];

export default function InsuranceIndicators({ dateRange }: InsuranceIndicatorsProps) {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, [dateRange]);

  const loadData = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (dateRange.start_date) params.append('start_date', dateRange.start_date);
      if (dateRange.end_date) params.append('end_date', dateRange.end_date);

      const queryString = params.toString();
      const url = queryString ? `/dashboard/analytics/insurance/?${queryString}` : '/dashboard/analytics/insurance/';
      const response = await api.get(url);
      setData(response.data);
    } catch (error) {
      console.error('Error loading insurance indicators:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center items-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div></div>;
  }

  if (!data) return null;

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <StatCard title="Patients Assurés" value={data.insured_patients} subtitle={`${data.insurance_rate?.toFixed(1)}% du total`} icon={<ShieldCheckIcon className="w-6 h-6" />} color="blue" />
        <StatCard title="Montant Facturé" value={`${data.total_insurance_amount?.toLocaleString()} GNF`} icon={<CurrencyDollarIcon className="w-6 h-6" />} color="green" />
        <StatCard title="Remboursé" value={`${data.total_reimbursed?.toLocaleString()} GNF`} icon={<CurrencyDollarIcon className="w-6 h-6" />} color="purple" />
        <StatCard title="En Attente" value={`${data.pending_reimbursement?.toLocaleString()} GNF`} icon={<ClockIcon className="w-6 h-6" />} color="yellow" />
      </div>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Top 5 Assurances</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={data.top_insurances} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" />
              <YAxis dataKey="name" type="category" width={120} />
              <Tooltip formatter={(value) => `${Number(value).toLocaleString()} GNF`} />
              <Bar dataKey="total_invoiced" fill="#3b82f6" name="Facturé" />
              <Bar dataKey="total_reimbursed" fill="#10b981" name="Remboursé" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Taux de Remboursement</h3>
          <GaugeChart
            value={data.reimbursement_rate || 0}
            max={100}
            label="Pourcentage remboursé"
            color={data.reimbursement_rate >= 80 ? 'green' : data.reimbursement_rate >= 60 ? 'yellow' : 'red'}
          />
          <div className="mt-4 text-center">
            <p className="text-sm text-gray-600">Délai moyen: {data.avg_reimbursement_delay} jours</p>
          </div>
        </div>
      </div>

      {/* Distribution Chart */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Répartition des Patients par Assurance</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={data.patients_by_insurance}
                dataKey="count"
                nameKey="insurance"
                cx="50%"
                cy="50%"
                outerRadius={100}
                label={({ insurance, percent }) => `${insurance}: ${(percent * 100).toFixed(0)}%`}
              >
                {data.patients_by_insurance?.map((entry: any, index: number) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Évolution des Factures Assurance</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={data.insurance_amount_evolution}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" tickFormatter={(value) => new Date(value).toLocaleDateString('fr-FR', { month: 'short' })} />
              <YAxis />
              <Tooltip labelFormatter={(value) => new Date(value).toLocaleDateString('fr-FR')} formatter={(value) => `${Number(value).toLocaleString()} GNF`} />
              <Line type="monotone" dataKey="total" stroke="#3b82f6" strokeWidth={2} name="Montant facturé" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Details Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Détails par Assurance</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Assurance</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Patients</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Facturé</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Remboursé</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Taux</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {data.top_insurances?.map((ins: any, index: number) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{ins.name}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{ins.patient_count}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{ins.total_invoiced?.toLocaleString()} GNF</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{ins.total_reimbursed?.toLocaleString()} GNF</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{ins.coverage_rate}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
